import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  BookOpen, Clock, PlayCircle, Home as HomeIcon, ShoppingBag, HeartPulse,
  Stethoscope, Syringe, Bath, Bed, ShieldAlert, ClipboardCheck, Check,
  CalendarDays, MessageCircle, Phone, ArrowRight, Utensils, Sparkles,
  AlertTriangle, Users, Repeat, ChevronRight,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';
import { CONTACT } from '../../data/mock';
import { trackCallClick, trackWhatsAppClick, trackBookButtonClick } from '../../lib/analytics';

const TITLE = 'Bringing Home a New Puppy or Kitten | First-Time Pet Parent Guide | Ichi & Ori Pet Care';
const DESCRIPTION = 'Bringing home a new puppy or kitten? Read our complete first-time pet parent guide covering home preparation, feeding, vaccinations, first vet visits, safety tips and everything you need to know.';
const CANONICAL = 'https://ichiandoripetcare.com/pet-care-guides/bringing-home-a-new-puppy-or-kitten';
const YT_ID = 'FKZvxu3CxG8';

const set = (sel, attr, val) => { const el = document.head.querySelector(sel); if (el) el.setAttribute(attr, val); };

const CHECKLIST = [
  { Icon: Utensils,      title: 'Food',                text: 'Age-appropriate food (ideally the same brand your breeder or shelter was using, for the first two weeks).' },
  { Icon: ShoppingBag,   title: 'Food and Water Bowls',text: 'Two shallow, non-slip bowls kept in a quiet corner, away from the litter tray or high-traffic areas.' },
  { Icon: Bed,           title: 'Bed',                 text: 'A soft, washable bed sized to feel snug. Puppies and kittens sleep 16 to 20 hours a day.' },
  { Icon: HomeIcon,      title: 'Crate or Carrier',    text: 'A well-ventilated carrier for travel and vet visits. Introduce it slowly with treats and blankets that smell like home.' },
  { Icon: Sparkles,      title: 'Toys',                text: 'A few chew-safe toys for puppies, and one wand or ball plus scratchers for kittens. Rotate to keep them interesting.' },
  { Icon: HeartPulse,    title: 'Collar or Harness',   text: 'A soft adjustable collar. For puppies, add a light harness for the first walks once vaccinations allow.' },
  { Icon: ClipboardCheck,title: 'Litter Tray (cats)',  text: 'A tray with low sides for kittens, unscented clumping litter, and a scoop. Keep it away from food and water.' },
  { Icon: Bath,          title: 'Cleaning Supplies',   text: 'Enzyme-based cleaner (not ammonia), paper towels and pee pads for the first weeks of toilet training.' },
  { Icon: ShieldAlert,   title: 'ID Tag',              text: 'A tag with your phone number, and a plan for microchipping during the first vet visit.' },
];

const PET_PROOFING = [
  { Icon: AlertTriangle, title: 'Electrical Wires',     text: 'Tuck them away or run them through cord protectors. Both puppies and kittens love chewing.' },
  { Icon: AlertTriangle, title: 'Toxic Foods',          text: 'Chocolate, grapes, raisins, onions, garlic, xylitol (sugar-free products), macadamia nuts, coffee, alcohol.' },
  { Icon: AlertTriangle, title: 'Toxic Plants',         text: 'Lilies (deadly for cats), pothos, sago palm, dieffenbachia, aloe vera, tulips, azalea. Move or rehome them.' },
  { Icon: AlertTriangle, title: 'Medicines',            text: 'Paracetamol is toxic to cats. Keep all human medicines in closed cabinets, never on bedside tables.' },
  { Icon: AlertTriangle, title: 'Cleaning Chemicals',   text: 'Floor cleaners, phenyl and disinfectants can burn paws and irritate lungs. Store high and rinse floors well after use.' },
  { Icon: Bed,           title: 'A Safe Sleeping Area', text: 'One consistent spot with a bed, water and low lighting. Avoid drafts, direct sun and loud appliances.' },
];

const FIRST_VET_VISIT = [
  { Icon: Stethoscope,    title: 'Physical Examination', text: 'A gentle nose-to-tail check for weight, temperature, hydration, eyes, ears, teeth, heart and abdomen.' },
  { Icon: Syringe,        title: 'Vaccination Planning', text: 'Your vet will draw up a schedule based on age and lifestyle, usually starting between 6 and 9 weeks.' },
  { Icon: HeartPulse,     title: 'Deworming',            text: 'A deworming plan for the first months, since most puppies and kittens carry intestinal worms from birth.' },
  { Icon: ShieldAlert,    title: 'Parasite Prevention',  text: 'Practical guidance on ticks, fleas and mites, and how to keep them off both your pet and your home.' },
  { Icon: Utensils,       title: 'Nutrition Advice',     text: 'How much to feed, how often, and when to transition to junior or adult food. Body-condition matters more than weight alone.' },
  { Icon: ClipboardCheck, title: 'Growth Monitoring',    text: 'A baseline weight, growth chart and clear signs to watch for at home over the following weeks.' },
];

const MISTAKES = [
  'Skipping the first vet visit because "the pet looks fine". Many issues are subtle and easier to catch early.',
  'Feeding cow’s milk to kittens or puppies. Most are lactose intolerant. Use kitten or puppy formula if needed.',
  'Punishing accidents during toilet training. It slows learning and damages trust. Reward the right behaviour instead.',
  'Bathing too early or too often. Wait until your vet gives the go-ahead and use only pet-safe shampoo.',
  'Overwhelming the new pet with visitors in the first week. Give them a quiet, boring first few days.',
  'Missing booster vaccinations. A single vaccine is not enough. The whole series matters.',
  'Buying a large collar that "they will grow into". Puppies and kittens grow fast and can slip out.',
  'Assuming a friendly pet does not need socialisation. Early, positive exposure is a foundation for adult behaviour.',
];

const FAQS = [
  { q: 'How old should my puppy or kitten be before I bring them home?',
    a: 'Ideally 8 weeks or older for both puppies and kittens. Before that, they are still learning important social skills from their mother and littermates. Very young pets can also struggle with feeding, temperature regulation and immunity.' },
  { q: 'When should I book the first vet visit?',
    a: 'Within the first 3 to 7 days of bringing your pet home, even if they seem perfectly healthy. The first visit sets up a vaccination schedule, deworming plan, and a baseline weight and growth check.' },
  { q: 'What vaccinations does a puppy need?',
    a: 'Core puppy vaccines protect against distemper, hepatitis, parvovirus and parainfluenza (often given together), along with rabies. The full series typically runs from 6 to 16 weeks. Your vet will personalise the schedule.' },
  { q: 'What vaccinations does a kitten need?',
    a: 'Core kitten vaccines protect against feline panleukopenia, herpesvirus and calicivirus (often combined), along with rabies. The series usually starts around 8 weeks and continues over the following months.' },
  { q: 'How often should I feed my new puppy or kitten?',
    a: 'Puppies under 3 months need 4 small meals a day. Kittens the same age often do best on 3 to 4 meals. Portion size varies with breed and body condition, so ask your vet for a personalised plan.' },
  { q: 'Is it safe to take my puppy outside before all vaccinations?',
    a: 'Full outdoor walks should wait until 7 to 10 days after the final puppy vaccine. Before that, socialise safely by carrying them, meeting fully vaccinated adult dogs at home, and letting them see the world from a stroller or your arms.' },
  { q: 'How long will it take for my new pet to settle in?',
    a: 'Most puppies and kittens begin to relax within 3 to 5 days. A fuller sense of routine and confidence usually takes 3 to 4 weeks. Some pets take longer, and that is completely normal.' },
  { q: 'Should I microchip my puppy or kitten?',
    a: 'Yes. Microchipping is a quick, low-discomfort procedure that gives you a lifelong way to reunite with a lost pet. Many families choose to have it done during a routine vet visit or at the time of neutering.' },
  { q: 'When should I start grooming my new pet?',
    a: 'From the very first week, at home. Short, positive handling sessions with a brush and gentle paw touches build tolerance for baths, nail trims and professional grooming later on.' },
  { q: 'What are early warning signs I should call the vet about?',
    a: 'Vomiting more than once or twice, diarrhoea for more than a day, refusing food for over 24 hours, unusual lethargy, laboured breathing, or bleeding of any kind. When in doubt, call. Early is always better than late.' },
];

const RELATED_GUIDES = [
  { title: 'Puppy Vaccination Schedule',     description: 'A calm, evidence-based schedule from your puppy’s first shots through the boosters.', to: '/puppy-vaccination-noida' },
  { title: 'Kitten Vaccination Schedule',    description: 'A gentle, cat-friendly approach to first vaccinations, deworming and parasite prevention.', to: '/kitten-vaccination-noida' },
  { title: 'Finding a Trusted Vet in Noida', description: 'What to look for in a family vet, and how the first few visits shape long-term trust.', to: '/veterinary-clinic-noida' },
];

const TOC = [
  { id: 'video',        label: 'Watch the Complete Guide' },
  { id: 'congrats',     label: 'Congratulations on Your New Family Member' },
  { id: 'before',       label: 'Before Bringing Your Pet Home' },
  { id: 'preparing',    label: 'Preparing Your Home' },
  { id: 'first-24',     label: 'The First 24 Hours' },
  { id: 'feel-safe',    label: 'Helping Your Pet Feel Safe' },
  { id: 'feeding',      label: 'Feeding Your Puppy or Kitten' },
  { id: 'toilet',       label: 'Toilet Training Basics' },
  { id: 'socialise',    label: 'Socialisation' },
  { id: 'first-vet',    label: 'Your Pet’s First Veterinary Visit' },
  { id: 'vaccines',     label: 'Vaccination Schedule' },
  { id: 'grooming',     label: 'Grooming Basics' },
  { id: 'boarding',     label: 'Boarding' },
  { id: 'mistakes',     label: 'Common First-Time Mistakes' },
  { id: 'faqs',         label: 'FAQs' },
];

const BringingHomePuppyOrKitten = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    const orig = {
      d: document.head.querySelector('meta[name="description"]')?.getAttribute('content'),
      c: document.head.querySelector('link[rel="canonical"]')?.getAttribute('href'),
    };
    set('meta[name="description"]', 'content', DESCRIPTION);
    set('link[rel="canonical"]', 'href', CANONICAL);
    set('meta[property="og:title"]', 'content', TITLE);
    set('meta[property="og:description"]', 'content', DESCRIPTION);
    set('meta[property="og:url"]', 'content', CANONICAL);
    set('meta[property="og:type"]', 'content', 'article');
    return () => {
      if (orig.d != null) set('meta[name="description"]', 'content', orig.d);
      if (orig.c != null) set('link[rel="canonical"]', 'href', orig.c);
    };
  }, []);

  const wa = `https://wa.me/${CONTACT.phoneRaw.replace(/\D/g, '')}?text=${encodeURIComponent("Hi, I'd like to book a first vet visit for my new puppy/kitten at Ichi & Ori Pet Care.")}`;

  const faqSchema = {
    '@context': 'https://schema.org', '@type': 'FAQPage',
    mainEntity: FAQS.map(f => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
  };
  const breadcrumbSchema = {
    '@context': 'https://schema.org', '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://ichiandoripetcare.com/' },
      { '@type': 'ListItem', position: 2, name: 'Pet Care Guides', item: 'https://ichiandoripetcare.com/pet-care-guides' },
      { '@type': 'ListItem', position: 3, name: 'Bringing Home a New Puppy or Kitten', item: CANONICAL },
    ],
  };
  const articleSchema = {
    '@context': 'https://schema.org', '@type': 'Article',
    headline: 'Bringing Home a New Puppy or Kitten: A Complete First-Time Pet Parent Guide',
    description: DESCRIPTION,
    author: { '@type': 'Organization', name: 'Ichi & Ori Pet Care' },
    publisher: {
      '@type': 'Organization', name: 'Ichi & Ori Pet Care',
      logo: { '@type': 'ImageObject', url: 'https://ichiandoripetcare.com/logo.png' },
    },
    mainEntityOfPage: CANONICAL,
    video: {
      '@type': 'VideoObject',
      name: 'Bringing Home a New Puppy or Kitten (Complete Guide)',
      description: DESCRIPTION,
      thumbnailUrl: `https://i.ytimg.com/vi/${YT_ID}/hqdefault.jpg`,
      uploadDate: '2024-01-01',
      embedUrl: `https://www.youtube.com/embed/${YT_ID}`,
    },
  };

  return (
    <div className="bg-white" data-testid="guide-puppy-kitten-page">
      <Helmet>
        <title>{TITLE}</title>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      </Helmet>

      {/* 1 — HERO */}
      <section className="relative bg-gradient-to-b from-[#fdf3fc] via-[#fdf7fb] to-white overflow-hidden">
        <div className="max-w-[900px] mx-auto px-5 sm:px-8 lg:px-10 pt-16 sm:pt-20 pb-12 sm:pb-16 text-center">
          {/* Breadcrumb */}
          <nav aria-label="Breadcrumb" className="mb-8 text-[12px] tracking-wider text-[#5b3eb5]/70 font-semibold uppercase">
            <ol className="inline-flex items-center gap-2 flex-wrap justify-center">
              <li><Link to="/" className="hover:text-[#e870b6] transition-colors">Home</Link></li>
              <li aria-hidden="true"><ChevronRight className="w-3 h-3 inline text-[#5b3eb5]/50" /></li>
              <li><span className="text-[#5b3eb5]">Pet Care Guides</span></li>
              <li aria-hidden="true"><ChevronRight className="w-3 h-3 inline text-[#5b3eb5]/50" /></li>
              <li><span className="text-[#e870b6]">First-Time Guide</span></li>
            </ol>
          </nav>

          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white border border-[#f3d6f5] text-[#5b3eb5] text-[11px] font-bold tracking-[0.22em] mb-8 shadow-sm">
            <BookOpen className="w-3.5 h-3.5" /> PET CARE GUIDES
          </span>
          <h1 className="text-[32px] sm:text-5xl lg:text-[56px] font-extrabold text-[#1a0b3d] leading-[1.05] mb-6" data-testid="guide-h1">
            Bringing Home a New Puppy or Kitten: A Complete First-Time Pet Parent Guide
          </h1>
          <p className="text-base sm:text-lg text-[#3b1d76]/85 leading-relaxed max-w-2xl mx-auto mb-6">
            A calm, practical guide for the first days, first month and first vet visit. Written for pet parents who want to get it right, without being overwhelmed by a hundred internet opinions.
          </p>
          <div className="inline-flex items-center gap-4 text-[13px] text-[#3b1d76]/70 flex-wrap justify-center">
            <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> 10 min read</span>
            <span aria-hidden="true">·</span>
            <span className="inline-flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> Written for first-time pet parents</span>
          </div>
        </div>
      </section>

      {/* MAIN LAYOUT */}
      <div className="max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-10 py-10 sm:py-14 grid grid-cols-1 lg:grid-cols-[1fr_260px] gap-10 lg:gap-14">

        <article className="min-w-0">

          {/* 2 — VIDEO */}
          <section id="video" className="mb-16 scroll-mt-24">
            <div className="flex items-center gap-2 mb-4">
              <PlayCircle className="w-5 h-5 text-[#e870b6]" />
              <span className="text-[11px] font-bold tracking-[0.22em] text-[#5b3eb5]">WATCH THE COMPLETE GUIDE</span>
            </div>
            <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#1a0b3d] leading-tight mb-3">Prefer to watch instead of read?</h2>
            <p className="text-[#3b1d76]/85 text-[15.5px] leading-relaxed mb-6">
              We recorded the whole guide on video. Watch it below, or keep scrolling to read the same information at your own pace.
            </p>
            <div className="relative w-full rounded-2xl overflow-hidden shadow-xl border border-[#f3d6f5] bg-black" style={{ aspectRatio: '16/9' }}>
              <iframe
                data-testid="guide-video"
                src={`https://www.youtube.com/embed/${YT_ID}?rel=0`}
                title="Bringing Home a New Puppy or Kitten (Complete Guide) by Ichi and Ori Pet Care"
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                referrerPolicy="strict-origin-when-cross-origin"
                className="absolute inset-0 w-full h-full"
              />
            </div>
          </section>

          {/* 3 — CONGRATS */}
          <section id="congrats" className="mb-14 scroll-mt-24">
            <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#1a0b3d] leading-tight mb-4">Congratulations on your new family member</h2>
            <div className="prose max-w-none text-[#3b1d76]/85 text-[15.5px] leading-[1.75] space-y-4">
              <p>Bringing home a puppy or kitten is exciting and, quite honestly, a little overwhelming. That is completely normal. In the first week you will second-guess a lot of small decisions, and that is a sign you care.</p>
              <p>This guide walks you through the essentials, from what to buy before your pet arrives to what to expect during the first vet visit. Read it once now, and come back to specific sections when you need them.</p>
            </div>
          </section>

          {/* 4 — BEFORE BRINGING HOME (CHECKLIST) */}
          <section id="before" className="mb-14 scroll-mt-24">
            <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#1a0b3d] leading-tight mb-3">Before bringing your pet home</h2>
            <p className="text-[#3b1d76]/85 text-[15.5px] leading-relaxed mb-8">
              A quick checklist to set up ahead of time. Nothing on this list is expensive, and none of it needs to be perfect.
            </p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="checklist">
              {CHECKLIST.map((c, i) => (
                <li key={i} className="flex gap-3 bg-[#fdf7fb] rounded-2xl p-5 border border-[#f3d6f5]/60">
                  <div className="w-10 h-10 rounded-xl bg-white border border-[#f5d3e6] flex items-center justify-center flex-shrink-0">
                    <c.Icon className="w-4.5 h-4.5 text-[#e870b6]" />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#1a0b3d] text-[15px] mb-1 leading-snug">{c.title}</h3>
                    <p className="text-[13.5px] text-[#3b1d76]/80 leading-relaxed">{c.text}</p>
                  </div>
                </li>
              ))}
            </ul>
          </section>

          {/* 5 — PREPARING YOUR HOME */}
          <section id="preparing" className="mb-14 scroll-mt-24">
            <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#1a0b3d] leading-tight mb-3">Preparing your home</h2>
            <p className="text-[#3b1d76]/85 text-[15.5px] leading-relaxed mb-8">
              Get down to your pet’s eye level. Anything they can reach with a paw, a mouth or a hop should be safe or moved. A few essentials to check:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {PET_PROOFING.map((p, i) => (
                <div key={i} className="flex gap-3 bg-white rounded-2xl p-5 border border-[#f3d6f5]/60 shadow-sm">
                  <div className="w-10 h-10 rounded-xl bg-[#fdf3fc] border border-[#f5d3e6] flex items-center justify-center flex-shrink-0">
                    <p.Icon className="w-4.5 h-4.5 text-[#5b3eb5]" />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#1a0b3d] text-[15px] mb-1 leading-snug">{p.title}</h3>
                    <p className="text-[13.5px] text-[#3b1d76]/80 leading-relaxed">{p.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* 6 — FIRST 24 HOURS */}
          <section id="first-24" className="mb-14 scroll-mt-24">
            <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#1a0b3d] leading-tight mb-4">The first 24 hours</h2>
            <div className="text-[#3b1d76]/85 text-[15.5px] leading-[1.75] space-y-4">
              <p>Your pet has just left everything familiar (their mother, littermates, smells, sounds). Expect a bit of hiding, some crying at night, and less interest in food than you might hope.</p>
              <p>Keep the first day quiet. Skip visitors, keep the TV low, and give your pet a small, safe part of the house to explore. Let them come to you.</p>
              <p>Offer water often. Offer food in small amounts. Watch, but do not hover. Most puppies and kittens sleep a lot in the first 48 hours, and that is exactly what they should do.</p>
            </div>
          </section>

          {/* 7 — FEEL SAFE */}
          <section id="feel-safe" className="mb-14 scroll-mt-24">
            <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#1a0b3d] leading-tight mb-4">Helping your pet feel safe</h2>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { Icon: HeartPulse, title: 'Build trust slowly',       text: 'Sit on the floor. Let them approach. Reward every brave choice with a small treat or gentle voice.' },
                { Icon: Repeat,     title: 'Keep a predictable routine',text: 'Same feeding times, same play times, same sleeping spot. Predictability lowers anxiety.' },
                { Icon: Bed,        title: 'Give them a quiet corner', text: 'A crate or a covered bed in a low-traffic area, where they can retreat without being followed.' },
                { Icon: Check,      title: 'Positive reinforcement',   text: 'Reward what you want to see, ignore what you can, redirect what you cannot. No punishment.' },
                { Icon: Users,      title: 'Avoid overwhelming days',  text: 'Skip big gatherings, long car rides and loud parks for the first two weeks.' },
              ].map((s, i) => (
                <li key={i} className="flex gap-3 bg-[#fdf7fb] rounded-2xl p-5 border border-[#f3d6f5]/60">
                  <div className="w-10 h-10 rounded-xl bg-white border border-[#f5d3e6] flex items-center justify-center flex-shrink-0">
                    <s.Icon className="w-4.5 h-4.5 text-[#e870b6]" />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#1a0b3d] text-[15px] mb-1 leading-snug">{s.title}</h3>
                    <p className="text-[13.5px] text-[#3b1d76]/80 leading-relaxed">{s.text}</p>
                  </div>
                </li>
              ))}
            </ul>
          </section>

          {/* 8 — FEEDING */}
          <section id="feeding" className="mb-14 scroll-mt-24">
            <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#1a0b3d] leading-tight mb-4">Feeding your puppy or kitten</h2>
            <div className="text-[#3b1d76]/85 text-[15.5px] leading-[1.75] space-y-4">
              <p>For the first two weeks, keep feeding the exact food your breeder or shelter used. Sudden changes cause digestive upsets, which can look alarming in a small pet.</p>
              <p>Puppies under three months usually eat four small meals a day. Kittens the same age often prefer three to four meals. Portion sizes vary with breed, size and activity level, so treat the bag guidance as a starting point rather than a rule.</p>
              <p>Fresh water at all times. Avoid cow’s milk, kitchen scraps, bones and human snacks. Ask your vet before adding treats or supplements.</p>
            </div>
          </section>

          {/* 9 — TOILET */}
          <section id="toilet" className="mb-14 scroll-mt-24">
            <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#1a0b3d] leading-tight mb-4">Toilet training basics</h2>
            <div className="text-[#3b1d76]/85 text-[15.5px] leading-[1.75] space-y-4">
              <p><strong>Puppies:</strong> Take them out (or to a pee pad) after every meal, every nap and every play session. Reward the second they finish in the right spot. Never scold accidents. Clean up with an enzyme cleaner so they do not return to the same spot.</p>
              <p><strong>Kittens:</strong> Most learn the litter tray on their own within a few days if the tray is easy to reach, unscented and cleaned once a day. If accidents happen, check the location, the litter type and whether one tray is enough. Older kittens often prefer two.</p>
              <p>Expect 4 to 8 weeks of ongoing training. Consistency matters more than any single trick.</p>
            </div>
          </section>

          {/* 10 — SOCIALISATION */}
          <section id="socialise" className="mb-14 scroll-mt-24">
            <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#1a0b3d] leading-tight mb-4">Socialisation: why early positive experiences matter</h2>
            <div className="text-[#3b1d76]/85 text-[15.5px] leading-[1.75] space-y-4">
              <p>The first 4 months shape a lot of a pet’s adult personality. Calm, positive exposure to different sounds, surfaces, people, and (once safely vaccinated) other pets, builds a confident, easy-to-live-with adult.</p>
              <p>What good socialisation looks like:</p>
              <ul className="list-disc pl-5 space-y-1.5">
                <li>Short sessions, not long ones. Five minutes of a new experience is often enough.</li>
                <li>Every new thing paired with a treat or a favourite toy.</li>
                <li>You watching your pet’s body language and stopping before they get overwhelmed.</li>
                <li>Never forcing a pet into an interaction. Let them approach, retreat and approach again.</li>
              </ul>
            </div>
          </section>

          {/* 11 — FIRST VET VISIT (KEY) */}
          <section id="first-vet" className="mb-16 scroll-mt-24 bg-[#fdf7fb] -mx-5 sm:-mx-8 lg:-mx-10 px-5 sm:px-8 lg:px-10 py-12 sm:py-14 border-y border-[#f3d6f5]/60 rounded-3xl">
            <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#1a0b3d] leading-tight mb-3">Your pet’s first veterinary visit</h2>
            <p className="text-[#3b1d76]/85 text-[15.5px] leading-relaxed mb-8">
              Try to schedule the first visit within the first week of your pet coming home, even if they look perfectly well. It is the single most important appointment of the first year. Here is what a thorough first visit includes:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
              {FIRST_VET_VISIT.map((v, i) => (
                <div key={i} className="flex gap-3 bg-white rounded-2xl p-5 border border-[#f3d6f5]/60 shadow-sm">
                  <div className="w-10 h-10 rounded-xl bg-[#fdf3fc] border border-[#f5d3e6] flex items-center justify-center flex-shrink-0">
                    <v.Icon className="w-4.5 h-4.5 text-[#5b3eb5]" />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#1a0b3d] text-[15px] mb-1 leading-snug">{v.title}</h3>
                    <p className="text-[13.5px] text-[#3b1d76]/80 leading-relaxed">{v.text}</p>
                  </div>
                </div>
              ))}
            </div>

            <p className="text-[#3b1d76]/85 text-[15.5px] leading-relaxed mb-6">
              At Ichi &amp; Ori Pet Care, the first visit is unhurried on purpose. If you are new to pet parenting, we would rather answer 20 basic questions than have you leave with three important ones unasked. You can read more about our approach on our <Link to="/veterinary-clinic-noida" className="text-[#5b3eb5] font-semibold underline underline-offset-2 hover:text-[#e870b6]">Veterinary Clinic Noida</Link> page, or explore specific care under <Link to="/dog-vet-noida" className="text-[#5b3eb5] font-semibold underline underline-offset-2 hover:text-[#e870b6]">Dog Vet Noida</Link>, <Link to="/cat-vet-noida" className="text-[#5b3eb5] font-semibold underline underline-offset-2 hover:text-[#e870b6]">Cat Vet Noida</Link> and <Link to="/pet-doctor-noida" className="text-[#5b3eb5] font-semibold underline underline-offset-2 hover:text-[#e870b6]">Pet Doctor Noida</Link>.
            </p>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
              <Link to="/book" onClick={() => trackBookButtonClick('guide_puppy_kitten_first_vet')} className="w-full sm:w-auto">
                <Button data-testid="first-vet-book-btn" className="w-full sm:w-auto bg-[#e870b6] hover:bg-[#d85ba0] text-white rounded-full px-7 py-6 text-base font-semibold shadow-lg">
                  <CalendarDays className="w-5 h-5 mr-2" /> Book the First Vet Visit
                </Button>
              </Link>
              <a href={wa} target="_blank" rel="noreferrer" onClick={() => trackWhatsAppClick('guide_puppy_kitten_first_vet')} className="text-[14px] font-semibold text-[#5b3eb5] hover:text-[#e870b6] underline underline-offset-4 transition-colors">
                Or ask us on WhatsApp first
              </a>
            </div>
          </section>

          {/* 12 — VACCINES */}
          <section id="vaccines" className="mb-14 scroll-mt-24">
            <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#1a0b3d] leading-tight mb-4">Vaccination schedule</h2>
            <div className="text-[#3b1d76]/85 text-[15.5px] leading-[1.75] space-y-4">
              <p>A single vaccine is rarely enough. Puppies and kittens receive a series of vaccines that build immunity gradually, then a booster around a year later, then ongoing yearly boosters as needed.</p>
              <p>Rather than duplicate the full schedules here, we have written dedicated, up-to-date guides:</p>
              <ul className="list-disc pl-5 space-y-1.5">
                <li><Link to="/puppy-vaccination-noida" className="text-[#5b3eb5] font-semibold underline underline-offset-2 hover:text-[#e870b6]">Puppy Vaccination</Link>, the full puppy series, week by week</li>
                <li><Link to="/kitten-vaccination-noida" className="text-[#5b3eb5] font-semibold underline underline-offset-2 hover:text-[#e870b6]">Kitten Vaccination</Link>, a calm, kitten-friendly approach</li>
                <li><Link to="/pet-vaccination-noida" className="text-[#5b3eb5] font-semibold underline underline-offset-2 hover:text-[#e870b6]">Pet Vaccination</Link>, the general overview for adult pets and boosters</li>
              </ul>
            </div>
          </section>

          {/* 13 — GROOMING */}
          <section id="grooming" className="mb-14 scroll-mt-24">
            <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#1a0b3d] leading-tight mb-4">Grooming basics</h2>
            <div className="text-[#3b1d76]/85 text-[15.5px] leading-[1.75] space-y-4">
              <p>The earlier a puppy or kitten learns to tolerate handling, the easier their whole life becomes. From week one, do short daily sessions of touching paws, opening the mouth briefly, checking ears and brushing. Pair each with treats and gentle praise.</p>
              <p>Bathing usually waits until after the first set of vaccines, and even then it should be occasional. Professional grooming can start once vaccinations allow. Read more about our approach to gentle, breed-appropriate grooming on the <Link to="/pet-grooming-noida" className="text-[#5b3eb5] font-semibold underline underline-offset-2 hover:text-[#e870b6]">Pet Grooming</Link> page.</p>
            </div>
          </section>

          {/* 14 — BOARDING */}
          <section id="boarding" className="mb-14 scroll-mt-24">
            <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#1a0b3d] leading-tight mb-4">When boarding becomes useful</h2>
            <div className="text-[#3b1d76]/85 text-[15.5px] leading-[1.75] space-y-4">
              <p>You do not need boarding from day one. Most families begin thinking about it around the 4 to 6 month mark, once the pet is fully vaccinated and used to being alone for short stretches.</p>
              <p>A short daytime stay before an overnight one is the gentlest way to introduce boarding. See how we handle stays for anxious first-timers on our <Link to="/pet-boarding-noida" className="text-[#5b3eb5] font-semibold underline underline-offset-2 hover:text-[#e870b6]">Pet Boarding</Link> page.</p>
            </div>
          </section>

          {/* 15 — MISTAKES */}
          <section id="mistakes" className="mb-14 scroll-mt-24">
            <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#1a0b3d] leading-tight mb-4">Common mistakes first-time pet parents make</h2>
            <p className="text-[#3b1d76]/85 text-[15.5px] leading-relaxed mb-6">You are going to make small mistakes, and that is okay. These are the ones that are worth knowing about in advance:</p>
            <ul className="space-y-3">
              {MISTAKES.map((m, i) => (
                <li key={i} className="flex gap-3 bg-white rounded-xl p-4 border border-[#f3d6f5]/60">
                  <span className="w-6 h-6 rounded-full bg-[#fdf3fc] text-[#e870b6] flex items-center justify-center text-[12px] font-bold flex-shrink-0 mt-0.5">{i + 1}</span>
                  <p className="text-[14.5px] text-[#3b1d76]/85 leading-relaxed">{m}</p>
                </li>
              ))}
            </ul>
          </section>

          {/* 16 — FAQs */}
          <section id="faqs" className="mb-14 scroll-mt-24">
            <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#1a0b3d] leading-tight mb-6">Frequently asked questions</h2>
            <Accordion type="single" collapsible defaultValue="faq-0" className="divide-y divide-[#f5d3e6] border border-[#f5d3e6] bg-white rounded-2xl px-4 sm:px-6" data-testid="guide-faq">
              {FAQS.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="px-1">
                  <AccordionTrigger data-testid={`faq-q-${i}`} className="text-left text-[#1a0b3d] font-semibold text-[15px] sm:text-[16px] hover:no-underline py-5">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-[#3b1d76]/85 text-[14.5px] sm:text-[15px] leading-relaxed pb-5">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          {/* 17 — RELATED GUIDES */}
          <section className="mb-14">
            <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#1a0b3d] leading-tight mb-6">Related guides &amp; care pages</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {RELATED_GUIDES.map((r, i) => (
                <Link key={i} to={r.to} data-testid={`related-guide-${i}`} className="group bg-[#fdf7fb] rounded-2xl p-6 border border-[#f3d6f5]/60 hover:border-[#e870b6]/60 hover:shadow-md transition-all flex flex-col">
                  <BookOpen className="w-5 h-5 text-[#e870b6] mb-3" />
                  <h3 className="font-bold text-[#1a0b3d] text-[16px] leading-snug mb-2">{r.title}</h3>
                  <p className="text-[13.5px] text-[#3b1d76]/80 leading-relaxed flex-1">{r.description}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-[13px] font-semibold text-[#5b3eb5] group-hover:text-[#e870b6] transition-colors">
                    Read guide <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                  </span>
                </Link>
              ))}
            </div>
          </section>

          {/* 18 — FINAL CTA */}
          <section className="mb-6">
            <div className="rounded-[28px] bg-gradient-to-br from-[#5b3eb5] to-[#1a0b3d] text-white py-12 sm:py-14 px-6 sm:px-10 relative overflow-hidden">
              <div className="absolute inset-0 opacity-25 [background-image:radial-gradient(circle_at_15%_25%,#e870b6_0%,transparent_45%)]" />
              <div className="relative">
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold mb-3 leading-tight">Ready for your pet’s first health check?</h2>
                <p className="text-white/85 text-[15px] sm:text-[16px] leading-relaxed mb-8 max-w-2xl">
                  Book a calm, unhurried first visit with our vets. We will walk you through vaccinations, deworming and your first few months, one honest step at a time.
                </p>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
                  <Link to="/book" onClick={() => trackBookButtonClick('guide_puppy_kitten_final')} className="w-full sm:w-auto">
                    <Button data-testid="final-book-btn" className="w-full sm:w-auto bg-[#e870b6] hover:bg-[#d85ba0] rounded-full px-7 py-6 font-semibold shadow-xl">
                      <CalendarDays className="w-5 h-5 mr-2" /> Request Appointment
                    </Button>
                  </Link>
                  <a href={wa} target="_blank" rel="noreferrer" onClick={() => trackWhatsAppClick('guide_puppy_kitten_final')} className="w-full sm:w-auto">
                    <Button data-testid="final-wa-btn" className="w-full sm:w-auto bg-[#25d366] hover:bg-[#1eb858] rounded-full px-7 py-6 font-semibold shadow-xl">
                      <MessageCircle className="w-5 h-5 mr-2" /> WhatsApp
                    </Button>
                  </a>
                  <a href={`tel:${CONTACT.phoneRaw}`} onClick={() => trackCallClick('guide_puppy_kitten_final')} className="w-full sm:w-auto sm:ml-1" data-testid="final-call-link">
                    <span className="inline-flex items-center gap-2 text-white/85 hover:text-white font-medium text-[14px] sm:text-[15px] underline decoration-white/30 underline-offset-4 hover:decoration-white transition-colors px-1 py-2">
                      <Phone className="w-4 h-4" /> Call Now
                    </span>
                  </a>
                </div>
                <p className="text-white/60 text-[13px] mt-6">
                  Prefer a home visit? See our <Link to="/home-veterinary-visit-noida" className="underline underline-offset-2 hover:text-white">Home Veterinary Visit</Link> service.
                </p>
              </div>
            </div>
          </section>

        </article>

        {/* STICKY TABLE OF CONTENTS (desktop only) */}
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <p className="text-[11px] font-bold tracking-[0.22em] text-[#5b3eb5] mb-4">ON THIS PAGE</p>
            <nav aria-label="On this page">
              <ol className="space-y-2 text-[13.5px]" data-testid="toc-list">
                {TOC.map((t, i) => (
                  <li key={t.id}>
                    <a href={`#${t.id}`} className="block py-1 text-[#3b1d76]/75 hover:text-[#e870b6] transition-colors leading-snug">
                      <span className="text-[#e870b6] font-bold mr-2">{String(i + 1).padStart(2, '0')}</span>{t.label}
                    </a>
                  </li>
                ))}
              </ol>
            </nav>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default BringingHomePuppyOrKitten;
